import { kindKey } from './inbox-kinds'
import { errorCopyKey } from '@/lib/inbox/error-copy'
import type { InboxEntry, InboxEntryKind, InboxLatest } from '@/types/inbox'

/** A message as the copy reads it: a full entry or a thread's newest message in brief. */
export type InboxCopySource = InboxEntry | InboxLatest

/** The named arguments a message template takes. */
export type InboxCopyArgs = Record<string, string | number>

/** The translator and number formatters the arguments are built with. */
export interface InboxCopyFormat {
  t: (key: string, named?: Record<string, unknown>, plural?: number) => string
  formatDecimal: (value: number, maximumFractionDigits?: number) => string
  formatFixed: (value: number, fractionDigits?: number) => string
}

const SUBJECT_MAX = 80
const LINE_MAX = 140

const PLATFORM_NAMES: Readonly<Record<string, string>> = { linkedin: 'LinkedIn' }

const RUN_NAME_KINDS: ReadonlySet<string> = new Set([
  'article',
  'post',
  'plan',
  'design',
  'design-edit',
  'brand-style',
])

const SUBJECT_VARIANT_KINDS: ReadonlySet<InboxEntryKind> = new Set([
  'article.ready',
  'plan.ready',
  'plan.reminder',
  'design.ready',
  'brandStyle.decided',
])

function yesNo(value: boolean): 'yes' | 'no' {
  return value ? 'yes' : 'no'
}

function variantOf(source: InboxCopySource): string | undefined {
  switch (source.kind) {
    case 'article.ready':
    case 'plan.ready':
      return source.params.outcome
    case 'plan.reminder':
      return source.params.stage === 'review-topics' ? 'reviewTopics' : 'reviewPlan'
    case 'design.ready':
      return yesNo(source.params.partial)
    case 'brandStyle.decided':
      return source.params.decision
    case 'post.draft':
      return yesNo(source.params.hook !== '')
    case 'research.ready':
      return source.params.runKind
    default:
      return undefined
  }
}

function sectionKey(
  section: 'subject' | 'line' | 'body',
  source: InboxCopySource,
  variant: string | undefined,
): string | null {
  if (source.kind === 'note') return null
  const base = `inbox.${section}.${kindKey(source.kind)}`
  return variant === undefined ? base : `${base}.${variant}`
}

/** The catalogue key of a message's subject template, variant segment included; null for a note. */
export function inboxSubjectKey(source: InboxCopySource): string | null {
  return sectionKey(
    'subject',
    source,
    SUBJECT_VARIANT_KINDS.has(source.kind) ? variantOf(source) : undefined,
  )
}

/** The catalogue key of a message's one-line template, variant segment included; null for a note. */
export function inboxLineKey(source: InboxCopySource): string | null {
  return sectionKey('line', source, variantOf(source))
}

/** The catalogue key of a message's body paragraphs (an array), variant segment included; null for a note. */
export function inboxBodyKey(source: InboxCopySource): string | null {
  return sectionKey('body', source, variantOf(source))
}

/** The catalogue key of a message's primary verb. */
export function inboxVerbKey(source: InboxCopySource): string {
  const base = `inbox.verb.${kindKey(source.kind)}`
  return source.kind === 'plan.reminder' ? `${base}.${variantOf(source)}` : base
}

/** The catalogue key of a message's secondary verb; null when the kind has none. */
export function inboxVerbSecondaryKey(source: InboxCopySource): string | null {
  return source.kind === 'post.publishFailed' ? `inbox.verbSecondary.${kindKey(source.kind)}` : null
}

/** The plural count that picks a template's form, or undefined for a message with a single form. */
export function inboxCopyPlural(source: InboxCopySource): number | undefined {
  switch (source.kind) {
    case 'article.questions':
      return source.params.questionCount
    case 'plan.topics':
      return source.params.topics
    default:
      return undefined
  }
}

function planChanges(
  params: { topicsChanged: number; pagesAdded: number; pagesRemoved: number; pagesChanged: number },
  { t }: InboxCopyFormat,
): string {
  const parts: Array<[string, number]> = [
    ['topics', params.topicsChanged],
    ['added', params.pagesAdded],
    ['removed', params.pagesRemoved],
    ['changed', params.pagesChanged],
  ]
  return parts
    .filter(([, count]) => count > 0)
    .map(([key, count]) => t(`inbox.changes.${key}`, { n: count }, count))
    .join(', ')
}

/** The named arguments a message's templates take, derived from its typed params. */
export function inboxCopyArgs(source: InboxCopySource, format: InboxCopyFormat): InboxCopyArgs {
  const { t, formatDecimal, formatFixed } = format
  switch (source.kind) {
    case 'article.questions':
      return { count: source.params.questionCount, page: source.params.pageTitle }
    case 'article.ready':
      return { page: source.params.pageTitle, outcome: source.params.outcome }
    case 'plan.proposal':
      return { changes: planChanges(source.params, format) }
    case 'plan.topics':
      return { count: source.params.topics }
    case 'plan.reminder':
      return {}
    case 'plan.ready':
      return { outcome: source.params.outcome }
    case 'post.draft':
      return {
        title: source.params.title,
        hook: source.params.hook,
        hooked: yesNo(source.params.hook !== ''),
      }
    case 'post.publishFailed':
      return { title: source.params.title, error: t(errorCopyKey(source.params.errorCode)) }
    case 'post.published':
      return { title: source.params.title }
    case 'post.results':
      return {
        title: source.params.title,
        impressions: formatDecimal(source.params.impressions, 0),
        reach: formatDecimal(source.params.reach, 0),
        clicks: formatDecimal(source.params.clicks, 0),
        rate: `${formatFixed(source.params.engagementRate * 100, 1)}%`,
      }
    case 'run.failed':
      return {
        run: t(
          `inbox.runName.${RUN_NAME_KINDS.has(source.params.runKind) ? source.params.runKind : 'other'}`,
        ),
        runKind: source.params.runKind,
        error: t(errorCopyKey(source.params.errorCode)),
      }
    case 'account.disconnected':
      return {
        platform: PLATFORM_NAMES[source.params.platform] ?? source.params.platform,
        error: t(errorCopyKey(source.params.errorCode)),
      }
    case 'research.ready':
      return {
        research: t(`inbox.researchKind.${source.params.runKind}`),
        runKind: source.params.runKind,
      }
    case 'design.ready':
      return { partial: yesNo(source.params.partial) }
    case 'brandStyle.ready':
      return { design: source.params.designName }
    case 'brandStyle.decided':
      return { decision: source.params.decision }
    case 'note':
      return {}
  }
}

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

function cut(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}...` : text
}

function noteLines(text: string): { first: string; rest: string } {
  const lines = text
    .split(/\r?\n/)
    .map(collapse)
    .filter((line) => line !== '')
  return { first: lines[0] ?? '', rest: lines.slice(1).join(' ') }
}

/** A note's text: the body of an entry, or the snippet of a thread's newest message. */
export function inboxNoteText(source: InboxCopySource): string {
  if (source.kind !== 'note') return ''
  return 'body' in source ? source.body : (source.snippet ?? '')
}

/** A note's subject: its first line, cut at 80 characters. */
export function inboxNoteSubject(text: string): string {
  return cut(noteLines(text).first, SUBJECT_MAX)
}

/** A note's one-line preview: the text after its first line, cut at 140 characters. */
export function inboxNoteLine(text: string): string {
  return cut(noteLines(text).rest, LINE_MAX)
}
